import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useLocation } from 'react-router-dom';

import { IconSearch } from '@tabler/icons-react';

import EmptyState from '@/components/EmptyState';
import Input from '@/components/Input/Input';
import Pagination from '@/components/Pagination';
import LessonBreakdownCardsSkeleton from '@/components/SkeletonLoader/LessonBreakdownCardsSkeleton';
import TagCapsule from '@/components/TagCapsule';
import { LessonStatusConfig } from '@/constants/constant';
import { ELessonStatus } from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import { getSchoolClassLessonBreakdownList } from '@/service/lessonService';
import { useSettingsStore } from '@/store/useSettingsStore';

interface ISchoolLessonBreakdown {
  id: string;
  title: string;
  total_plays: number;
  completed_plays: number;
  average_progress: number;
  status: ELessonStatus;
}

const PAGE_SIZE = 12;

const SchoolLessonBreakdownList = () => {
  const { classId } = useParams<{ classId: string }>();
  const location = useLocation();
  const { formatText } = useFormatText();
  const { setIsBreadcrumb } = usePageTitle();
  const { settings } = useSettingsStore();

  const className =
    (location.state as { className?: string } | null)?.className ?? '';

  const [lessons, setLessons] = useState<ISchoolLessonBreakdown[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  const { register, watch } = useForm({ defaultValues: { search: '' } });
  const search = watch('search');
  const debouncedSearch = useDebounce(search, 500);

  // Reset to page 1 on new search
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  // Breadcrumbs
  useEffect(() => {
    setIsBreadcrumb([
      {
        label: formatText('breadcrumb.teachers'),
        to: '/school/teachers-classes?tab=classes',
      },
      {
        label: className || '...',
        to: classId ? `/school/teachers-classes/class-details/${classId}` : '',
        state: { className },
      },
      { label: formatText('classDetails.lessons.title') },
    ]);

    return () => setIsBreadcrumb([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, className]);

  // Fetch data
  useEffect(() => {
    if (!classId) return;

    const fetchLessons = async () => {
      setIsLoading(true);
      try {
        const response = await getSchoolClassLessonBreakdownList(classId, {
          search: debouncedSearch || undefined,
          limit: PAGE_SIZE,
          offset: (currentPage - 1) * PAGE_SIZE,
        });

        if (response?.status) {
          setLessons(response.data ?? []);
          setTotal(response.total ?? 0);
        } else {
          setLessons([]);
          setTotal(0);
        }
      } catch (error) {
        console.error(error);
        setLessons([]);
        setTotal(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLessons();
  }, [classId, debouncedSearch, currentPage]);

  const renderContent = () => {
    if (isLoading) {
      return <LessonBreakdownCardsSkeleton count={PAGE_SIZE} />;
    }

    if (lessons.length === 0) {
      return (
        <EmptyState
          title={formatText('common.no-data-found')}
          className="h-75"
        />
      );
    }

    return (
      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {lessons.map((lesson) => {
            const config = lesson.status
              ? LessonStatusConfig[lesson.status]
              : null;

            return (
              <div
                key={lesson.id}
                className="border border-neutral-200 rounded-2xl p-4 flex-1 h-full flex items-center"
              >
                <div className="flex items-center justify-between h-full w-full">
                  <div className="flex flex-col gap-0.5 max-w-[80%]">
                    <h3 className="text-base font-semibold text-neutral-primary">
                      {lesson.title}
                    </h3>
                    <p className="text-sm text-neutral-secondary">
                      {lesson.completed_plays}/{lesson.total_plays}{' '}
                      {formatText('classDetails.lessons.completed')}
                    </p>
                  </div>
                  {config && (
                    <TagCapsule
                      value={formatText(config?.label ? config.label : '')}
                      className={config.className ?? ''}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {total > PAGE_SIZE && (
          <Pagination
            total={total}
            currentPage={currentPage}
            pageSize={PAGE_SIZE}
            setCurrentPage={setCurrentPage}
            className="border-t-0!"
          />
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Search */}
      <div className="flex items-center gap-3 ps-1">
        <Input
          name="search"
          register={register}
          placeholder={formatText('classDetails.lessons.search-placeholder')}
          leftIcon={{
            icon: <IconSearch size={20} className="text-neutral-500" />,
          }}
          className="w-full max-w-75"
          inputClassName="py-[13px]"
        />
      </div>
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-semibold text-neutral-primary">
          {formatText('classDetails.lessons.title')}
        </h2>
        <div
          className="inline-flex items-center px-2 py-0.5 rounded-full bg-neutral-100"
          data-testid="lessons-count"
        >
          <span className="text-xs font-semibold text-neutral-primary">
            {String(total ?? 0).padStart(2, '0')}
          </span>
        </div>
      </div>
      {renderContent()}
    </div>
  );
};

export default SchoolLessonBreakdownList;
